import React, { FC, useRef, useState, useCallback } from 'react'
import { usePopper } from 'react-popper'
import { Icon2 } from '@elamajs/ui-kit'

import useOnClickOutside from '@src/hooks/useOnClickOutside'
import { usePopupAnimation } from '../../hooks'

import {
  TriggerButton,
  StyledTooltip,
  Content,
  tooltipExitOpacity,
  tooltipEnterOpacity,
  Arrow
} from './Tooltip.styled'
import { TooltipProps } from './Tooltip.types'

const Tooltip: FC<TooltipProps> = ({ zIndex = 1, dataTest, className, children, direction }) => {
  const reference = useRef<HTMLButtonElement>(null)
  const popper = useRef<HTMLDivElement>(null)
  const arrow = useRef<HTMLDivElement>(null)

  const { styles, attributes } = usePopper(reference.current, popper.current, {
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrow.current
        }
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10]
        }
      }
    ],
    placement: direction
  })

  const [isOpened, setOpened] = useState(false)
  const [openStatus, onAnimationEnd] = usePopupAnimation(isOpened, {
    enterAnimationName: tooltipEnterOpacity.getName(),
    exitAnimationName: tooltipExitOpacity.getName()
  })

  useOnClickOutside(popper, () => setOpened(false))

  const onTriggerClick = useCallback(() => {
    setOpened(!isOpened)
  }, [isOpened])

  return (
    <>
      <TriggerButton
        ref={reference}
        onClick={onTriggerClick}
        isOpened={isOpened}
        data-test={dataTest}
        className={className}
      >
        <Icon2 icon="questionNew" />
      </TriggerButton>
      <StyledTooltip
        ref={popper}
        style={styles.popper}
        zIndex={zIndex}
        openStatus={openStatus}
        onAnimationEnd={onAnimationEnd}
        {...attributes.popper}
      >
        <Content>{children}</Content>
        <Arrow
          ref={arrow}
          style={styles.arrow}
          data-placement={attributes?.popper?.['data-popper-placement']}
        />
      </StyledTooltip>
    </>
  )
}

Tooltip.displayName = 'Tooltip'

export default Tooltip
